import React, {Component, Fragment} from 'react';
import {connect} from "react-redux";
import H from "history";
import Table, {TableColumn} from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import {IRootState} from '../../../store';
import {TableRowProps} from '../../TableRow';
import {cellForColumn} from './SubmissionAssignmentAnalyticsUtils';
import {SubjectSubmissionSummary} from '../../../models/analytics/AssignmentSubmissionSummaries';
import AcademicYearPicker from '../../AcademicYearPicker';
import SchoolMenu from '../../SchoolMenu';
import {PrimaryButton} from 'office-ui-fabric-react/lib-commonjs';
import SchoolPageTitle from '../../SchoolPageTitle';
import ClassSubmissionResponse from "../../../models/analytics/ClassSubmissionResponse";
import PercentageOverviewDoughnut from "../../assignment-analytics/PercentageOverviewDoughnut";
import SchoolStudentSubmissonAnalyticsPage from "./SchoolStudentSubmissonAnalyticsPage";
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";

interface ClassSubmissionAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
    classId: string;
}

interface ClassSubmissionAnalyticsPageState {
    tableVisible: "assignments" | "students";
    result?: ClassSubmissionResponse;
}

class ClassSubmissionAnalyticsPage extends Component<ClassSubmissionAnalyticsPageProps & DispatchProps, ClassSubmissionAnalyticsPageState> {
    static displayName = ClassSubmissionAnalyticsPage.name;
    
    private static readonly assignmentColumns: TableColumn[] = [
        { title: "Assignment Name", propertyPath: "displayName", enableSort: true, flex: 4 },
        { title: "Status", propertyPath: "status", enableSort: true, flex: 1 },
        { title: "Created", propertyPath: "createdDateTime",type: "date", enableSort: true, flex: 1 },
        { title: "Due", propertyPath: "dueDateTime", type:"date", enableSort: true, flex: 1 },
    ];
    
    constructor(props: ClassSubmissionAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = {
            result: undefined,
            tableVisible: "assignments"
        }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear && this.props.classId) {
            await this.getResult();
        }
    }

    async componentDidUpdate(prevProps: Readonly<ClassSubmissionAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear || this.props.classId !== prevProps.classId) {
            await this.getResult();
        }
    }

    private async getResult(): Promise<void> {
        this.setState({
            result: undefined
        });

        const result = await this.props.apiService.getClassSubmissionAnalytics(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!,this.props.classId);

        this.setState({
            result
        });
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private readonly rowProps = (item: SubjectSubmissionSummary): TableRowProps => {
        return {
            enableRowLink: false
        };
    }

    private buildCharts = (): JSX.Element | undefined => {
        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{marginTop: "0.25rem"}}>
                <div className={"number-stat"}>
                    <div className="number-stat__number number-stat__number--small">
                        {this.state.result?.assignments.length}
                    </div>
                    <div className={"number-stat__description"}>Assignments</div>
                </div>
                
                
                <PercentageOverviewDoughnut percentageOverview={this.state.result!.completedPercentageOverview} title={"% Students completing assignments"} labels={PercentageOverviewDoughnut.LABELS_ALWAYS}/>
                <PercentageOverviewDoughnut percentageOverview={this.state.result!.onTimePercentageOverview} title={"% Students completing assignments on time"} labels={PercentageOverviewDoughnut.LABELS_ALWAYS}/>
            </div>
        );
    }

    private buildTables(): JSX.Element {
        
        let table: JSX.Element | undefined = undefined;
        
        if(this.state.tableVisible === "assignments")
        {
            table = (
                <Table
                    key={"assignmentsTable"}
                    title={"Assignments"}
                    items={this.state.result!.assignments}
                    columns={ClassSubmissionAnalyticsPage.assignmentColumns}
                    cellForColumn={cellForColumn}
                    rowPropsForItem={this.rowProps}
                    defaultSortColumnPath={''}
                    enableSort={true}>
                </Table>
            );
        }
        else if(this.state.tableVisible === "students")
        {
            table = (
                <Table
                    key={"studentsTable"}
                    title={"Students"}
                    items={this.state.result!.students}
                    columns={SchoolStudentSubmissonAnalyticsPage.columns}
                    cellForColumn={cellForColumn}
                    rowPropsForItem={this.rowProps}
                    defaultSortColumnPath={''}
                    enableSort={true}>
                </Table>
            )
        }
        
        return (
            <Fragment>
                <div className={"submission-analytics-navigation m-left-1rem"}>
                    <fieldset>
                        <legend>Data for class</legend>
                        <div className={"submission-analytics-navigation__links"}>
                            <PrimaryButton
                                text="Assignments"
                                checked={this.state.tableVisible === "assignments"}
                                onClick={() => this.setState({tableVisible:"assignments"})}
                            />
                            <PrimaryButton
                                text="Students"
                                checked={this.state.tableVisible === "students"}
                                onClick={() => this.setState({tableVisible:"students"})}
                            />
                        </div>
                    </fieldset>
                
                </div>
                {table}
            </Fragment>
        );
    }
    
    private buildResults(): JSX.Element
    {
        if (this.state.result === undefined)
        {
            return <LoadingCard />;
        }
        else
        {
            return (
                <Fragment>
                    {this.buildCharts()}
                    {this.buildTables()}
                </Fragment>
            );
        }
    }

    render() {

        
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title={`Assignment Analytics - ${this.state.result?.summary.classDisplayName}`} reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix={this.state.result?.summary.classDisplayName} />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} current={"subject"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): ClassSubmissionAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            classId: ownProps.match.params.classId,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(ClassSubmissionAnalyticsPage)
